// @ts-strict-ignore
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { TradeTicket } from '../../TradeTicket/TradeTicket';
import { Flex } from '../..';
import style from './style.module.scss';
import { AnimatedModal } from '../AnimatedModal';
import { Order } from 'phoenix/redux/models';
import { convertOrderToViewModel, getDefaultOrderProps, useTradeTicketViewModel } from 'components/TradeTicket/Store/useTradeTicketViewModel';
import { TradeTicketViewModel } from 'components/TradeTicket/Store/TradeTicketViewModel';
import { useAssetClassMetaV2 } from 'phoenix/models/AssetClasses/useAssetClass';
import { useSnexStore } from 'phoenix/hooks/UseSnexStore';
import { useText } from 'phoenix/hooks/UseText';
import { useMarketTimeSegmentV2 } from 'phoenix/hooks/useMarketTimeSegment';
import { GetTradeTicketQuote } from 'components/TradeTicket/Shared/helpers';
import { SetSelectedAccount } from 'components/AccountDropdown/Store/AccountSelectionStore';
import { Typography } from '@mui/material';
import { MultiLegOptionTradeTicket } from 'components/MultiLegTradeTicket/MultiLegOptionTradeTicket';
import { getMultiLegOrderPrice } from 'components/OpenOrders/helpers';
import { Snex1English } from 'phoenix/assets/lang/Snex1English';
import { ApiCreditDebit } from 'phoenix/models/ApiTradeRequest';
import { useSecurityMetadataV2 } from 'phoenix/stores/SecurityMetadataV2Store';

interface EditTradeModalProps {
    isOpen: boolean;
    order: Order;
    toggleModal: (nextOpenState: boolean) => void;
    onSuccess?: () => void;
    isDuplicate?: boolean;
}

export const EditTradeModal = ({ isOpen, order, toggleModal, isDuplicate }: EditTradeModalProps): ReactElement => {
    const [orderAgain, setOrderAgain] = useState<boolean | null>(null);
    const assetClass = useAssetClassMetaV2(order?.symbol);
    const [marketTimeSegment] = useMarketTimeSegmentV2();
    const quote = GetTradeTicketQuote(order?.symbol);
    const lang = useText((s) => s);
    const meta = useSecurityMetadataV2().getMetadataBySymbol(order?.symbol);
    const initialViewModel = useMemo(() => convertOrderToViewModel({ lang, meta, order }), [lang, meta, order]);
    const viewModel = useTradeTicketViewModel<TradeTicketViewModel>();
    const isMultiLeg = order?.complexOrders?.length > 0;
    const text = lang;
    const bannerMessage = orderAgain ? text.orders.orderAgain : orderAgain === false ? text.orders.editOrder : '';

    useEffect(() => {
        if (isOpen) {
            setOrderAgain(isDuplicate);
        }
    }, [isOpen, isDuplicate]);

    const {
        checkTradeFieldChanged,
        getViewModel,
        initialLimitPrice,
        initialQuantity,
        initialQuantityQualifier,
        initialStopPrice,
        limitPrice,
        modifyingOrder,
        orderType,
        quantity,
        quantityQualifier,
        stopPrice,
        setViewModel,
        state,
        timeInForce,
        variant
    } = viewModel;

    useEffect(() => {
        if (isOpen) {
            SetSelectedAccount(order?.accountNumber);
            const newVariant = variant === 'option-individual' ? 'option-individual' : 'normal';
            setViewModel({
                ...initialViewModel,
                modifyingOrder: !isDuplicate,
                state: 'input',
                variant: newVariant,
                initialViewModel
            });
        }
    }, [assetClass.family, assetClass.type, initialViewModel, isOpen, order, setViewModel, variant]);

    useEffect(() => {
        if (state === 'input' && (isDuplicate || modifyingOrder)) {
            checkTradeFieldChanged();
        }
    }, [
        state,
        modifyingOrder,
        setViewModel,
        getViewModel,
        initialViewModel,
        initialLimitPrice,
        initialQuantity,
        initialQuantityQualifier,
        initialStopPrice,
        limitPrice,
        orderType,
        quantity,
        quantityQualifier,
        stopPrice,
        timeInForce,
        checkTradeFieldChanged,
        isDuplicate
    ]);

    const handleToggleModal = useCallback(() => {
        setViewModel({
            ...getDefaultOrderProps({ assetClass, marketTimeSegment, quote }),
            modifyingOrder: !isDuplicate,
            hasModifyingOrderChanged: false,
            state: 'input'
        });
        toggleModal(false);
        setOrderAgain(null);
    }, [setViewModel, assetClass, marketTimeSegment, quote, toggleModal]);

    useEffect(() => {
        if (isMultiLeg && order?.spreadInstructions) {
            const { creditDebit } = getMultiLegOrderPrice(Snex1English.tradeTicket.input, order?.spreadInstructions);

            let apiCreditDebit: ApiCreditDebit = 'Even';

            if (creditDebit === Snex1English.tradeTicket.input.options.credit) {
                apiCreditDebit = 'Credit';
            } else if (creditDebit === Snex1English.tradeTicket.input.options.debit) {
                apiCreditDebit = 'Debit';
            } else if (creditDebit === Snex1English.tradeTicket.input.options.even) {
                apiCreditDebit = 'Even';
            }

            setViewModel({ debitCredit: apiCreditDebit });
        }
    }, [setViewModel, isMultiLeg, order?.spreadInstructions]);

    return (
        <AnimatedModal open={isOpen} onClose={handleToggleModal}>
            <Flex center className={style.ticketWrapper}>
                <Typography variant='h4' style={{ marginBottom: '10px', textAlign: 'center' }}>
                    {bannerMessage}
                </Typography>
            </Flex>
            <Flex center className={style.ticketWrapper}>
                {isMultiLeg ? <MultiLegOptionTradeTicket orderAgain={orderAgain} /> : <TradeTicket />}
            </Flex>
        </AnimatedModal>
    );
};
